import http, { request } from './http';

export interface Result<T> {
  success: boolean;
  message?: string;
  data: T;
}

function assertResult<T>(res: Result<T>): T {
  if (!res.success) {
    throw new Error(res.message || '请求失败');
  }
  return res.data;
}

/** 与后端 /api/knowledge/search 对齐（tenant_id 可由后端补全，前端仍会主动带上配置值） */
export interface KnowledgeSearchRequest {
  tenant_id?: string;
  query: string;
  kb_ids?: string[];
  top_k?: number;
  tags?: string[];
  scope_types?: string[];
  scope_ids?: string[];
}

export interface KnowledgeSearchHit {
  documentId?: string;
  document_id?: string;
  kbId?: string;
  kb_id?: string;
  tenantId?: string;
  tenant_id?: string;
  scopeType?: string;
  scope_type?: string;
  scopeId?: string;
  scope_id?: string;
  title?: string;
  source?: string;
  snippet?: string;
  score?: number;
  tags?: string[];
}

export interface KnowledgeSearchResult {
  hits: KnowledgeSearchHit[];
  rawData?: unknown;
}

export interface CreateKnowledgeBasePayload {
  name: string;
  description?: string | null;
  scope_type: 'company' | 'project' | 'personal';
  scope_id: string;
  company_id?: string | null;
  project_id?: string | null;
  department_id?: string | null;
  owner_user_id?: string | null;
  metadata_json?: Record<string, unknown> | null;
  is_active?: boolean;
  tenant_id?: string;
}

/** Agent 租户（AI_AGENT_TENANT_ID），与业务库 tenant_N 独立 */
let cachedAgentTenantId: string | null = null;
let agentTenantInflight: Promise<string> | null = null;

export async function getKnowledgeAgentTenantId(): Promise<string> {
  if (cachedAgentTenantId) {
    return cachedAgentTenantId;
  }
  if (!agentTenantInflight) {
    agentTenantInflight = (async () => {
      const res = await request<Result<{ tenant_id?: string }>>(http.get('/knowledge/agent-tenant'));
      const data = assertResult(res);
      const tid = String(data?.tenant_id ?? '').trim() || 'YDE';
      cachedAgentTenantId = tid;
      return tid;
    })().finally(() => {
      agentTenantInflight = null;
    });
  }
  return agentTenantInflight;
}

async function withTenantQueryParams<T extends Record<string, unknown>>(
  params?: T,
): Promise<T & { tenant_id: string }> {
  const tenant_id = await getKnowledgeAgentTenantId();
  return { ...(params as T), tenant_id };
}

export async function listKnowledgeBases(params: {
  scope_type?: string;
  scope_id?: string;
}): Promise<unknown> {
  const query = await withTenantQueryParams({ ...params });
  const res = await request<Result<unknown>>(http.get('/knowledge/bases', { params: query }));
  return assertResult(res);
}

export async function createKnowledgeBase(body: CreateKnowledgeBasePayload): Promise<unknown> {
  const tenant_id = await getKnowledgeAgentTenantId();
  const res = await request<Result<unknown>>(
    http.post('/knowledge/bases', { ...body, tenant_id }, { timeout: 120_000 }),
  );
  return assertResult(res);
}

export async function updateKnowledgeBase(
  kbId: string,
  body: Record<string, unknown>,
): Promise<unknown> {
  const params = await withTenantQueryParams();
  const res = await request<Result<unknown>>(
    http.put(`/knowledge/bases/${encodeURIComponent(kbId)}`, body, { params }),
  );
  return assertResult(res);
}

export async function deleteKnowledgeBase(kbId: string): Promise<unknown> {
  const params = await withTenantQueryParams();
  const res = await request<Result<unknown>>(
    http.delete(`/knowledge/bases/${encodeURIComponent(kbId)}`, { params }),
  );
  return assertResult(res);
}

export async function listKnowledgeDocuments(params: { kb_id?: string; management?: boolean }): Promise<unknown> {
  const query = await withTenantQueryParams({ ...params });
  const res = await request<Result<unknown>>(
    http.get('/knowledge/documents', { params: query, timeout: 120_000 }),
  );
  return assertResult(res);
}

export async function getKnowledgeDocumentStatus(
  docId: string,
  params?: { kb_id?: string },
): Promise<unknown> {
  const id = String(docId ?? '').trim();
  if (!id || id.startsWith('local-')) {
    throw new Error('文档不存在');
  }
  const query = await withTenantQueryParams({ ...params });
  const res = await request<Result<unknown>>(
    http.get(`/knowledge/documents/${encodeURIComponent(id)}/status`, {
      params: query,
      timeout: 60_000,
    }),
  );
  return assertResult(res);
}

export async function deleteKnowledgeDocument(
  docId: string,
  params?: { kb_id?: string },
): Promise<unknown> {
  const query = await withTenantQueryParams({ ...params });
  const res = await request<Result<unknown>>(
    http.delete(`/knowledge/documents/${encodeURIComponent(docId)}`, { params: query }),
  );
  return assertResult(res);
}

export async function updateKnowledgeDocument(
  docId: string,
  body: { title?: string; tags?: string[] | string; description?: string;
    accessMode?: string; accessUserIds?: number[]; navigationMode?: string; navigationUserIds?: number[] },
  kbId?: string,
): Promise<unknown> {
  const payload: Record<string, unknown> = { ...body };
  if (typeof body.tags === 'string') {
    payload.tags = body.tags
      .split(/[,，、]/)
      .map((t) => t.trim())
      .filter(Boolean);
  }
  const params = await withTenantQueryParams({ kb_id: kbId });
  // 三方 Agent 要求 tags 为 string[]，不可传逗号拼接字符串
  const res = await request<Result<unknown>>(
    http.put(`/knowledge/documents/${encodeURIComponent(docId)}`, payload, { params }),
  );
  return assertResult(res);
}

export async function reprocessKnowledgeDocument(docId: string, body?: { reprocess?: boolean }): Promise<unknown> {
  const params = await withTenantQueryParams();
  const res = await request<Result<unknown>>(
    http.post(`/knowledge/documents/${encodeURIComponent(docId)}/process`, body ?? { reprocess: true }, {
      params,
      timeout: 600_000,
    }),
  );
  return assertResult(res);
}

export async function uploadKnowledgeDocument(form: {
  kb_id: string;
  title: string;
  source?: string;
  created_by?: string;
  tags?: string;
  extra_metadata?: string;
  scope_type?: string;
  scope_id?: string;
  company_id?: string;
  project_id?: string;
  file: File;
}): Promise<unknown> {
  const tenant_id = await getKnowledgeAgentTenantId();
  const fd = new FormData();
  fd.append('tenant_id', tenant_id);
  fd.append('kb_id', form.kb_id);
  fd.append('title', form.title);
  fd.append('file', form.file);
  if (form.source) fd.append('source', form.source);
  if (form.created_by) fd.append('created_by', form.created_by);
  if (form.tags) fd.append('tags', form.tags);
  if (form.extra_metadata) fd.append('extra_metadata', form.extra_metadata);
  if (form.scope_type) fd.append('scope_type', form.scope_type);
  if (form.scope_id) fd.append('scope_id', form.scope_id);
  if (form.company_id) fd.append('company_id', form.company_id);
  if (form.project_id) fd.append('project_id', form.project_id);
  const res = await request<Result<unknown>>(
    http.post('/knowledge/documents/upload', fd, { timeout: 600_000 }),
  );
  return assertResult(res);
}

export async function searchKnowledgeStructured(payload: KnowledgeSearchRequest): Promise<KnowledgeSearchResult> {
  const tenant_id = await getKnowledgeAgentTenantId();
  const res = await request<Result<KnowledgeSearchResult>>(
    http.post('/knowledge/search', { ...payload, tenant_id }),
  );
  return assertResult(res);
}

export async function getDocumentPermissionCandidates(docId: string, kbId: string): Promise<Array<{id: number; name: string}>> {
  const params = await withTenantQueryParams({ kb_id: kbId });
  const res = await request<Result<Array<{id: number; name: string}>>>(
    http.get(`/knowledge/documents/${encodeURIComponent(docId)}/permission-candidates`, { params }),
  );
  return assertResult(res);
}
